import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { useEffect, useState, type ReactNode } from 'react';
import { configureApiClient } from '@pantry/api-client';
import { AuthProvider } from '../contexts/AuthContext';
import { useAuth } from '../contexts/useAuth';
import { ThemeProvider } from '../contexts/ThemeContext';
import { HouseholdProvider, useSelectedHousehold } from '../contexts/HouseholdContext';
import { I18nProvider } from '../contexts/I18nContext';
import { queryClient } from '../lib/queryClient';
import { useAnalytics } from '../hooks/useAnalytics';
import { usePageViewTracking } from '../hooks/usePageViewTracking';

function ApiClientConfigurator({ children }: { children: ReactNode }) {
  const { getToken } = useAuth();
  const [configured, setConfigured] = useState(false);

  useEffect(() => {
    configureApiClient({
      baseUrl: import.meta.env.VITE_API_URL || 'http://localhost:3000',
      getToken,
    });
    // Using requestAnimationFrame to defer setState to avoid synchronous updates in effect
    requestAnimationFrame(() => setConfigured(true));
  }, [getToken]);

  if (!configured) return null;
  return <>{children}</>;
}

/**
 * Handles PostHog user identification and page view tracking.
 * Must be inside AuthProvider and HouseholdProvider.
 */
function AnalyticsIdentifier({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { selectedHousehold } = useSelectedHousehold();
  const { identify, reset } = useAnalytics();

  // Track page views
  usePageViewTracking();

  // Identify user when auth state changes
  useEffect(() => {
    if (user) {
      identify(user.id, {
        email: user.email ?? undefined,
        name: user.user_metadata?.name as string | undefined,
        subscription_tier: selectedHousehold?.subscriptionTier ?? 'free',
        household_id: selectedHousehold?.id,
      });
    } else {
      reset();
    }
  }, [user, selectedHousehold, identify, reset]);

  return <>{children}</>;
}

export function AppProviders({ children }: { children: ReactNode }) {
  return (
    <ThemeProvider>
      <AuthProvider>
        <QueryClientProvider client={queryClient}>
          <ApiClientConfigurator>
            <HouseholdProvider>
              <AnalyticsIdentifier>
                <I18nProvider>{children}</I18nProvider>
              </AnalyticsIdentifier>
            </HouseholdProvider>
          </ApiClientConfigurator>
          <ReactQueryDevtools initialIsOpen={false} />
        </QueryClientProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}
